window.themeLog = window.themeLog || function () {};
/**
 * PREDICTIVE SEARCH - Shopify Theme
 *
 * Features:
 * - Triggers after 3+ characters
 * - Calls Shopify Predictive Search API
 * - Renders product results with images
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Click outside to close
 * - Accessible with ARIA labels
 *
 * Usage: Automatically initializes on DOMContentLoaded
 */

class PredictiveSearch {
  constructor(inputId = 'search-input', resultsId = 'predictive-search-results', wrapperSelector = '.search-wrapper') {
    this.searchInput = document.getElementById(inputId);
    this.resultsContainer = document.getElementById(resultsId);
    this.resultsContent = this.resultsContainer?.querySelector('.predictive-search-content, .mobile-predictive-search-content');
    this.statusElement = this.resultsContainer?.querySelector('.predictive-search-status, .mobile-predictive-search-status');
    this.wrapperSelector = wrapperSelector;

    this.minCharacters = 3;
    this.debounceTimer = null;
    this.debounceDelay = 300;
    this.currentQuery = '';
    this.selectedIndex = -1;
    this.abortController = null;

    // Debug logging
    themeLog(`Predictive Search Elementen (${inputId}):`, {
      searchInput: this.searchInput,
      resultsContainer: this.resultsContainer,
      resultsContent: this.resultsContent,
      statusElement: this.statusElement
    });

    if (!this.searchInput) {
      console.warn(`⚠️ Search input niet gevonden! Zorg dat id="${inputId}" bestaat.`);
      return;
    }

    if (!this.resultsContainer) {
      console.warn(`⚠️ Results container niet gevonden! Zorg dat id="${resultsId}" bestaat.`);
      return;
    }

    if (this.searchInput && this.resultsContainer) {
      this.init();
      themeLog(`✅ Predictive Search event listeners toegevoegd voor ${inputId}`);
    }
  }

  init() {
    // Input event listener
    this.searchInput.addEventListener('input', (e) => {
      this.handleInput(e.target.value);
    });

    // Focus event
    this.searchInput.addEventListener('focus', () => {
      if (this.currentQuery.length >= this.minCharacters) {
        this.showResults();
      }
    });

    // Keyboard navigation
    this.searchInput.addEventListener('keydown', (e) => {
      this.handleKeyboard(e);
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!e.target.closest(this.wrapperSelector)) {
        this.hideResults();
      }
    });

    // Form submit handler - don't submit if selecting from dropdown
    this.searchInput.closest('form').addEventListener('submit', (e) => {
      if (this.selectedIndex >= 0) {
        e.preventDefault();
        this.selectResult();
      }
    });
  }

  handleInput(value) {
    const query = value.trim();

    // Clear previous timer
    clearTimeout(this.debounceTimer);

    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Hide results if less than min characters
    if (query.length < this.minCharacters) {
      this.hideResults();
      this.currentQuery = '';
      return;
    }

    // Don't search if query hasn't changed
    if (query === this.currentQuery) {
      return;
    }

    this.currentQuery = query;

    // Show loading state
    this.showLoading();

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceDelay);
  }

  async performSearch(query) {
    try {
      // Create abort controller for this request
      this.abortController = new AbortController();

      // Build API URL - met queries (zoeksuggesties) en products (max 3)
      // Include SKU, vendor, and tags in search fields
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=query,product&resources[limit]=3&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag`;

      // Fetch results
      const response = await fetch(url, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        // Log detailed error for debugging
        console.error('Predictive Search API Error:', {
          status: response.status,
          statusText: response.statusText,
          url: url
        });
        throw new Error(`Search request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Debug: log de response
      themeLog('Predictive Search Response:', data);

      // Render results
      this.renderResults(data, query);

    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }

      console.error('Predictive search error:', error);
      themeLog('💡 TIP: Predictive Search werkt alleen op een live Shopify shop, niet lokaal.');
      this.showError();
    }
  }

  renderResults(data, query) {
    // Extract queries en products uit response
    let queries = data.resources?.results?.queries || [];
    let products = data.resources?.results?.products || [];

    themeLog('Zoeksuggesties (van API):', queries.length, queries);
    themeLog('Products gevonden:', products.length, products);

    // FALLBACK: Als API geen queries geeft, genereer suggesties op basis van products
    if (queries.length === 0 && products.length > 0) {
      queries = this.generateQuerySuggestions(query, products);
      themeLog('Zoeksuggesties (gegenereerd):', queries.length, queries);
    }

    // Sorteer producten: met reviews eerst, max 3
    products = products
      .sort((a, b) => {
        const aHasReviews = this.hasReviews(a);
        const bHasReviews = this.hasReviews(b);

        if (aHasReviews && !bHasReviews) return -1;
        if (!aHasReviews && bHasReviews) return 1;
        return 0;
      })
      .slice(0, 3);

    // Limiteer zoeksuggesties tot max 5
    queries = queries.slice(0, 5);

    // Als geen resultaten, toon empty state
    if (queries.length === 0 && products.length === 0) {
      this.renderEmptyState(query);
      return;
    }

    // Reset selection
    this.selectedIndex = -1;

    // Build HTML
    let html = '';

    // SECTIE 1: ZOEKSUGGESTIES
    if (queries.length > 0) {
      html += `
        <div class="predictive-search-section">
          <div class="predictive-search-section-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
              <path d="M20 20l-4-4" stroke="currentColor" stroke-width="2"/>
            </svg>
            <span>Zoeksuggesties</span>
          </div>
          <div class="predictive-search-queries">
      `;

      queries.forEach((queryItem, index) => {
        html += `
          <a href="/search?q=${encodeURIComponent(queryItem.text)}"
             class="predictive-search-query"
             data-index="${index}"
             tabindex="-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
              <path d="M20 20l-4-4" stroke="currentColor" stroke-width="2"/>
            </svg>
            <span>${this.highlightQuery(queryItem.text, query)}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="arrow">
              <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2"/>
            </svg>
          </a>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    // SECTIE 2: PRODUCTEN
    if (products.length > 0) {
      html += `
        <div class="predictive-search-section">
          <div class="predictive-search-section-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
              <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2"/>
            </svg>
            <span>Producten</span>
          </div>
          <div class="predictive-search-products">
      `;

      products.forEach((product, index) => {
        const price = this.formatPrice(product.price);

        // Compare price: alleen tonen als groter dan 0 én groter dan normale prijs
        let comparePrice = null;
        if (product.compare_at_price_max) {
          const comparePriceNum = parseFloat(product.compare_at_price_max);
          const priceNum = parseFloat(product.price);
          if (comparePriceNum > 0 && comparePriceNum > priceNum) {
            comparePrice = this.formatPrice(product.compare_at_price_max);
          }
        }

        const imageUrl = product.image ? product.image : '';
        const available = product.available;

        html += `
          <a href="${product.url}"
             class="predictive-search-product"
             data-index="${queries.length + index}"
             tabindex="-1">
            <div class="predictive-search-product-image">
              ${imageUrl ? `
                <img src="${imageUrl}"
                     alt="${this.escapeHtml(product.title)}"
                     loading="lazy"
                     width="50"
                     height="50">
              ` : `
                <div class="predictive-search-product-placeholder">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
              `}
            </div>
            <div class="predictive-search-product-info">
              <h4 class="predictive-search-product-title">${this.highlightQuery(product.title, query)}</h4>
              ${this.getReviewsHTML(product)}
              <div class="predictive-search-product-price-wrapper">
                <div class="predictive-search-product-price">
                  ${comparePrice ? `
                    <div class="predictive-search-product-price-old">
                      <span class="label">Adviesprijs</span>
                      <span class="amount">${comparePrice}</span>
                    </div>
                    <div class="predictive-search-product-price-new">${price}</div>
                  ` : `
                    <div class="predictive-search-product-price-current">${price}</div>
                  `}
                </div>
                <div class="predictive-search-product-badges">
                  ${this.getDiscountBadge(product.price, product.compare_at_price_max)}
                  ${!available ? '<span class="predictive-search-product-badge predictive-search-product-badge--out-of-stock">Uitverkocht</span>' : ''}
                </div>
              </div>
            </div>
          </a>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    this.resultsContent.innerHTML = html;
    this.showResults();

    // Update status for screen readers
    const totalResults = queries.length + products.length;
    this.updateStatus(`${totalResults} resultaten gevonden (${queries.length} suggesties, ${products.length} producten)`);
  }

  renderEmptyState(query) {
    const html = `
      <div class="predictive-search-empty">
        <svg viewBox="0 0 24 24" fill="none" width="48" height="48">
          <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
          <path d="M20 20l-4-4" stroke="currentColor" stroke-width="2"/>
        </svg>
        <h4>Geen resultaten gevonden</h4>
        <p>Probeer een andere zoekterm voor "${this.escapeHtml(query)}"</p>
      </div>
    `;

    this.resultsContent.innerHTML = html;
    this.showResults();

    // Update status for screen readers
    this.updateStatus('Geen resultaten gevonden');
  }

  showLoading() {
    this.resultsContent.innerHTML = `
      <div class="predictive-search-loading">
        <div class="predictive-search-spinner"></div>
        <p>Zoeken...</p>
      </div>
    `;
    this.showResults();
    this.updateStatus('Aan het zoeken...');
  }

  showError() {
    this.resultsContent.innerHTML = `
      <div class="predictive-search-empty">
        <svg viewBox="0 0 24 24" fill="none" width="48" height="48">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2"/>
        </svg>
        <h4>Er is iets misgegaan</h4>
        <p>Probeer het opnieuw of gebruik de volledige zoekpagina.</p>
        <p style="font-size: 12px; color: #999; margin-top: 8px;">
          <em>Note: Predictive Search werkt alleen op een live Shopify shop.</em>
        </p>
      </div>
    `;
    this.showResults();
  }

  showResults() {
    this.resultsContainer.classList.add('active');
    this.searchInput.setAttribute('aria-expanded', 'true');
  }

  hideResults() {
    this.resultsContainer.classList.remove('active');
    this.searchInput.setAttribute('aria-expanded', 'false');
    this.selectedIndex = -1;
  }

  handleKeyboard(e) {
    // Selecteer alle clickable items (queries + products)
    const allItems = this.resultsContent?.querySelectorAll('.predictive-search-query, .predictive-search-product');

    if (!allItems || allItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, allItems.length - 1);
        this.updateSelection(allItems);
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(allItems);
        break;

      case 'Enter':
        if (this.selectedIndex >= 0) {
          e.preventDefault();
          this.selectResult();
        }
        break;

      case 'Escape':
        e.preventDefault();
        this.hideResults();
        this.searchInput.blur();
        break;
    }
  }

  updateSelection(items) {
    // Remove previous selection
    items.forEach(item => {
      item.classList.remove('selected');
      item.setAttribute('aria-selected', 'false');
    });

    // Add current selection
    if (this.selectedIndex >= 0) {
      const selected = items[this.selectedIndex];
      selected.classList.add('selected');
      selected.setAttribute('aria-selected', 'true');
      selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

      // Update status for screen readers
      const titleElement = selected.querySelector('.predictive-search-product-title, span');
      const title = titleElement ? titleElement.textContent : '';
      this.updateStatus(`Geselecteerd: ${title}`);
    }
  }

  selectResult() {
    const allItems = this.resultsContent?.querySelectorAll('.predictive-search-query, .predictive-search-product');
    if (allItems && this.selectedIndex >= 0) {
      const selected = allItems[this.selectedIndex];
      window.location.href = selected.href;
    }
  }

  updateStatus(message) {
    if (this.statusElement) {
      this.statusElement.textContent = message;
    }
  }

  formatPrice(price) {
    // Check of price een string of number is
    let priceNum;

    if (typeof price === 'string') {
      // Als het een string is, parse naar float
      priceNum = parseFloat(price);
    } else if (typeof price === 'number') {
      // Als het een number is, check of het in centen is (> 100 meestal)
      // Als > 1000, waarschijnlijk in centen
      priceNum = price > 1000 ? price / 100 : price;
    } else {
      return '€0,00';
    }

    // Format naar 2 decimalen
    const formatted = priceNum.toFixed(2);
    return `€${formatted}`.replace('.', ',');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  highlightQuery(text, query) {
    const escaped = this.escapeHtml(text);
    const regex = new RegExp(`(${query})`, 'gi');
    return escaped.replace(regex, '<mark>$1</mark>');
  }

  hasReviews(product) {
    // Check verschillende mogelijke review velden
    return (
      (product.reviews_count && product.reviews_count > 0) ||
      (product.rating && product.rating > 0) ||
      (product.metafields?.reviews?.rating?.value) ||
      false
    );
  }

  getReviewsHTML(product) {
    // Probeer rating en count te vinden
    const rating = product.rating || product.metafields?.reviews?.rating?.value || 0;
    const count = product.reviews_count || product.metafields?.reviews?.count?.value || 0;

    if (!rating || rating === 0) return '';

    // Genereer hartjes zoals EasyToys (5 hartjes max)
    const fullHearts = Math.floor(rating);
    const hasHalfHeart = rating % 1 >= 0.5;
    let heartsHTML = '';

    for (let i = 0; i < 5; i++) {
      if (i < fullHearts) {
        heartsHTML += '♥'; // Gevuld hartje
      } else if (i === fullHearts && hasHalfHeart) {
        heartsHTML += '♡'; // Half hartje (outline)
      } else {
        heartsHTML += '♡'; // Leeg hartje
      }
    }

    return `
      <div class="predictive-search-product-reviews">
        <span class="hearts">${heartsHTML}</span>
        ${count > 0 ? `<span class="count">(${count})</span>` : ''}
      </div>
    `;
  }

  getDiscountBadge(price, comparePrice) {
    if (!comparePrice) return '';

    const priceNum = parseFloat(price);
    const comparePriceNum = parseFloat(comparePrice);

    if (comparePriceNum <= priceNum) return '';

    const discount = Math.round(((comparePriceNum - priceNum) / comparePriceNum) * 100);

    return `<span class="predictive-search-product-badge predictive-search-product-badge--discount">-${discount}%</span>`;
  }

  generateQuerySuggestions(query, products) {
    themeLog('🔧 Genereer suggesties voor:', query, 'op basis van', products.length, 'producten');

    // Genereer intelligente zoeksuggesties op basis van gevonden producten
    const suggestions = new Set();
    const queryLower = query.toLowerCase().trim();

    // Voeg de exacte query toe
    suggestions.add(query);

    try {
      products.forEach(product => {
        if (!product.title) return;

        // Extract relevante woorden uit product title
        const title = product.title.toLowerCase();
        const words = title.split(/\s+/).filter(w => w.length > 0);

        themeLog('  Product:', product.title);

        // Voeg relevante woorden toe
        words.forEach((word, index) => {
          // Verwijder leestekens
          word = word.replace(/[^\w\s]/g, '');

          // Skip korte woorden en woorden zonder de query
          if (word.length < 3 || !word.includes(queryLower)) return;

          // Voeg het woord toe
          suggestions.add(word);
          themeLog('    + Suggestie:', word);

          // Voeg 2-woord combinaties toe
          if (index < words.length - 1) {
            const nextWord = words[index + 1].replace(/[^\w\s]/g, '');
            const twoWords = `${word} ${nextWord}`;
            if (twoWords.includes(queryLower)) {
              suggestions.add(twoWords);
              themeLog('    + Suggestie (2-woord):', twoWords);
            }
          }
        });

        // Voeg product type toe als suggestie
        if (product.product_type) {
          const productType = product.product_type.toLowerCase().trim();
          if (productType.includes(queryLower) && productType.length > 0) {
            suggestions.add(productType);
            themeLog('    + Suggestie (type):', productType);
          }
        }

        // Voeg vendor toe als suggestie
        if (product.vendor) {
          const vendor = product.vendor.toLowerCase().trim();
          if (vendor.includes(queryLower) && vendor.length > 0) {
            suggestions.add(vendor);
            themeLog('    + Suggestie (vendor):', vendor);
          }
        }
      });
    } catch (error) {
      console.error('Fout bij genereren suggesties:', error);
    }

    // Converteer naar array en format naar query objecten
    const result = Array.from(suggestions)
      .filter(text => text && text.length > 0)
      .slice(0, 5) // Max 5 suggesties
      .map(text => ({
        text: text,
        styled_text: text,
        url: `/search?q=${encodeURIComponent(text)}`
      }));

    themeLog('✅ Gegenereerde suggesties:', result);
    return result;
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    themeLog('🔍 Predictive Search wordt geïnitialiseerd...');
    // Desktop search
    const desktopInstance = new PredictiveSearch('search-input', 'predictive-search-results', '.search-wrapper');
    // Mobile search drawer
    const mobileInstance = new PredictiveSearch('mobile-search-drawer-input', 'mobile-predictive-search-results', '.mobile-search-drawer');
    themeLog('✅ Predictive Search actief!', { desktop: desktopInstance, mobile: mobileInstance });
  });
} else {
  themeLog('🔍 Predictive Search wordt geïnitialiseerd (DOM ready)...');
  // Desktop search
  const desktopInstance = new PredictiveSearch('search-input', 'predictive-search-results', '.search-wrapper');
  // Mobile search drawer
  const mobileInstance = new PredictiveSearch('mobile-search-drawer-input', 'mobile-predictive-search-results', '.mobile-search-drawer');
  themeLog('✅ Predictive Search actief!', { desktop: desktopInstance, mobile: mobileInstance });
}
